/**
 * Your tickets (step 82, `plan.md#step-82`) — the rules both sides keep.
 *
 * A raver's own tickets to a rave they attend: the file they uploaded (a
 * picture or a whole PDF), and each code read from it — its barcode format and
 * its value — so the app can draw it again, offline, at the gate.
 */

/** The largest file kept: a phone photo or an e-ticket PDF fits with room. */
export const TICKET_MAX_FILE_BYTES = 15 * 1024 * 1024;

/** The most codes saved from one file (a PDF with a group's tickets). Not a cap per rave — there is none. */
export const TICKET_MAX_CODES_PER_FILE = 50;

export const TICKET_MAX_NAME = 80;
export const TICKET_MAX_DESCRIPTION = 300;
/** A QR code holds at most 7,089 characters; base64 of binary content is longer than its bytes. */
export const TICKET_MAX_VALUE = 10_000;

/**
 * The barcode formats a ticket may be saved in — the READER's names for them
 * (zxing-cpp 3, `zxing-wasm`): any code MyRave can read is kept (the user,
 * decision C). Most it can also draw again (`TICKET_DRAWN_AS`); one it cannot
 * is shown as the original picture.
 */
export const TICKET_FORMATS = [
  'QRCode', 'QRCodeModel1', 'QRCodeModel2', 'MicroQRCode', 'RMQRCode', 'Aztec', 'AztecCode', 'AztecRune',
  'PDF417', 'CompactPDF417', 'MicroPDF417', 'DataMatrix', 'MaxiCode',
  'Code128', 'Code39', 'Code39Std', 'Code39Ext', 'Code32', 'PZN', 'Code93', 'Codabar', 'ITF', 'ITF14',
  'EAN13', 'EAN8', 'EAN5', 'EAN2', 'EANUPC', 'ISBN', 'UPCA', 'UPCE',
  'DataBar', 'DataBarOmni', 'DataBarStk', 'DataBarStkOmni', 'DataBarLtd', 'DataBarExp', 'DataBarExpStk',
  'Telepen', 'TelepenAlpha', 'TelepenNumeric', 'DXFilmEdge',
] as const;
export type TicketFormat = (typeof TICKET_FORMATS)[number];

export function isTicketFormat(f: string): f is TicketFormat {
  return (TICKET_FORMATS as readonly string[]).includes(f);
}

/**
 * The format to DRAW a read code in, or null when MyRave shows only the
 * original (the writer's names differ: it reads "Aztec", it writes
 * "AztecCode"; a UPC-A reads back as EAN-13 and is drawn as what it was read as).
 */
export const TICKET_DRAWN_AS: Readonly<Partial<Record<TicketFormat, string>>> = {
  QRCode: 'QRCode', QRCodeModel2: 'QRCode', MicroQRCode: 'MicroQRCode', RMQRCode: 'RMQRCode',
  Aztec: 'AztecCode', AztecCode: 'AztecCode', PDF417: 'PDF417', DataMatrix: 'DataMatrix',
  Code128: 'Code128', Code39: 'Code39', Code39Std: 'Code39Std', Code93: 'Code93', Codabar: 'Codabar', ITF: 'ITF',
  EAN13: 'EAN13', EAN8: 'EAN8', UPCA: 'UPCA', UPCE: 'UPCE',
};

/**
 * The formats that still read with the MyRave logo over their middle: QR only,
 * drawn at its highest error correction (H). Aztec was planned too, but its
 * centre IS its finder — with the logo it never read back (2026-10-28). Every
 * other code is drawn as it is, with no logo (decision C).
 */
export function ticketTakesLogo(format: string): boolean {
  return format === 'QRCode' || format === 'QRCodeModel2';
}

/**
 * The details printed on a ticket (the user, 2026-10-28, decision O2): fixed fields plus "other".
 * Kept as printed — text, never converted — read on the phone (a PDF's own text, else text
 * recognition) and corrected by the raver. Sealed on the server like the code's value.
 */
export const TICKET_DETAIL_FIELDS = [
  'holderName', 'idNumber', 'ticketNumber', 'orderNumber', 'ticketType', 'price', 'purchaseDate', 'other',
] as const;
export type TicketDetailField = (typeof TICKET_DETAIL_FIELDS)[number];
export type TicketDetails = Partial<Record<TicketDetailField, string>>;
/** One printed field; "other" holds the rest. */
export const TICKET_MAX_DETAIL = 200;
export const TICKET_MAX_OTHER = 1000;

/** Only known fields, trimmed, empty ones dropped — null when nothing is left. */
export function cleanTicketDetails(d: Readonly<Record<string, unknown>> | null | undefined): TicketDetails | null {
  if (!d) return null;
  const out: TicketDetails = {};
  for (const f of TICKET_DETAIL_FIELDS) {
    const v = d[f];
    if (typeof v !== 'string') continue;
    const t = v.trim().slice(0, f === 'other' ? TICKET_MAX_OTHER : TICKET_MAX_DETAIL);
    if (t) out[f] = t;
  }
  return Object.keys(out).length ? out : null;
}

/** What may be uploaded: a picture the server can strip, or a PDF — read from the bytes, never the name. */
export type TicketFileType = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';

export function sniffTicketFile(b: Uint8Array): TicketFileType | null {
  if (b.length >= 5 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46 && b[4] === 0x2d) return 'application/pdf';
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
    && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'image/webp';
  return null;
}

/**
 * Text recognition's reading models (step 82 O1, the user 2026-10-29: "more reading languages —
 * absolutely yes"): Tesseract's language codes, every one the app ships. English reads every ticket;
 * the others are fetched only when a ticket needs them.
 */
export const TICKET_OCR_LANGUAGES = [
  'eng', 'heb', 'ara', 'fas', 'spa', 'por', 'deu', 'fra', 'ita', 'nld', 'rus', 'ukr', 'bul', 'srp', 'srp_latn',
  'ell', 'tur', 'pol', 'ces', 'slk', 'hun', 'ron', 'hrv', 'dan', 'swe', 'lav', 'sqi', 'mlt', 'afr',
  'jpn', 'kor', 'chi_sim', 'chi_tra', 'hin', 'tha', 'khm',
] as const;
export type TicketOcrLanguage = (typeof TICKET_OCR_LANGUAGES)[number];

/** The app's languages (`UI_LANGUAGES`) as reading models. */
const OCR_OF_LANGUAGE: Readonly<Record<string, TicketOcrLanguage>> = {
  he: 'heb', ar: 'ara', fa: 'fas', es: 'spa', pt: 'por', de: 'deu', fr: 'fra', it: 'ita', nl: 'nld', ru: 'rus',
  uk: 'ukr', el: 'ell', tr: 'tur', pl: 'pol', cs: 'ces', hu: 'hun', ja: 'jpn', ko: 'kor', zh: 'chi_sim', hi: 'hin', th: 'tha',
};

/** What a country's tickets are printed in, besides English — the most used first. */
const OCR_OF_COUNTRY: Readonly<Record<string, readonly TicketOcrLanguage[]>> = {
  IL: ['heb'], DE: ['deu'], AT: ['deu'], CH: ['deu', 'fra'], LI: ['deu'], FR: ['fra'], BE: ['nld', 'fra'], LU: ['fra', 'deu'],
  MC: ['fra'], NL: ['nld'], ES: ['spa'], PT: ['por'], BR: ['por'], IT: ['ita'], SM: ['ita'], GR: ['ell'], CY: ['ell', 'tur'],
  TR: ['tur'], PL: ['pol'], CZ: ['ces'], SK: ['slk'], HU: ['hun'], RO: ['ron'], MD: ['ron'], HR: ['hrv'], BA: ['hrv', 'srp'],
  RS: ['srp_latn', 'srp'], ME: ['srp_latn'], BG: ['bul'], MK: ['bul'], RU: ['rus'], BY: ['rus'], KZ: ['rus'], UA: ['ukr'],
  GE: ['rus'], DK: ['dan'], SE: ['swe'], NO: ['dan'], LV: ['lav'], AL: ['sqi'], XK: ['sqi'], MT: ['mlt'], ZA: ['afr'], NA: ['afr'],
  MX: ['spa'], AR: ['spa'], CL: ['spa'], CO: ['spa'], PE: ['spa'], CR: ['spa'], PA: ['spa'], UY: ['spa'], EC: ['spa'],
  VE: ['spa'], BO: ['spa'], PY: ['spa'], GT: ['spa'], DO: ['spa'], CU: ['spa'], PR: ['spa'], CA: ['fra'],
  JP: ['jpn'], KR: ['kor'], CN: ['chi_sim'], TW: ['chi_tra'], HK: ['chi_tra'], MO: ['chi_tra'], SG: ['chi_sim'],
  TH: ['tha'], KH: ['khm'], IN: ['hin'], IR: ['fas'], AE: ['ara'], SA: ['ara'], QA: ['ara'], BH: ['ara'], KW: ['ara'],
  OM: ['ara'], JO: ['ara'], LB: ['ara', 'fra'], EG: ['ara'], MA: ['ara', 'fra'], TN: ['ara', 'fra'], DZ: ['ara', 'fra'],
};

/** At most this many models at once: each one makes reading slower. */
export const TICKET_OCR_MAX_LANGUAGES = 3;

/**
 * Which models read a ticket for a rave: English, the rave's COUNTRY's language(s), then the
 * READER's own — a Hebrew-speaking raver's ticket to Boom may be printed in Hebrew by an Israeli
 * seller. At most `TICKET_OCR_MAX_LANGUAGES`, English first.
 */
export function ticketReadingLanguages(ctx: { country?: string | null; language?: string | null }): TicketOcrLanguage[] {
  const out: TicketOcrLanguage[] = ['eng'];
  const add = (l: TicketOcrLanguage | undefined) => { if (l && !out.includes(l) && out.length < TICKET_OCR_MAX_LANGUAGES) out.push(l); };
  const country = OCR_OF_COUNTRY[(ctx.country ?? '').toUpperCase()] ?? [];
  add(country[0]);
  add(OCR_OF_LANGUAGE[(ctx.language ?? '').toLowerCase().split('-')[0]!]);
  for (const l of country.slice(1)) add(l);
  return out;
}
